import Axios, { AxiosResponse } from 'axios';
import TrackResponseInterface from '@/webcomponents/f1-season/store/interface/track';
import JsonLd from '@/webcomponents/f1-season/store/interface/jsonLd';
import ChampionshipResponseInterface from '@/webcomponents/f1-season/store/interface/championship';
import {
  ActionContext,
  ActionTree, CommitOptions, DispatchOptions,
  GetterTree,
  Module,
  MutationTree,
} from 'vuex';
import { RootState } from '@/rootStore';
import AppConst from '../const';
import Event from './entity/Event';
import Track from './entity/Track';
import Championship from './entity/Championship';
import { sortRaceResults } from '../helpers/sort';
import EventResponseInterface, {
  EventSessionInterface,
  SessionResultInterface,
} from './interface/event';

interface FetchTrackPayload {
  raceTrack: string;
  raceTrackEventIndex: number;
}

export type State = {
  isLoading: boolean;
  championships: Array<Championship>;
  events: Array<Event>;
  event: Event | undefined;
  track: Track | undefined;
  seasonYear: number;
}

export enum MutationTypes {
  SET_LOADING_STATE = 'SET_LOADING_STATE',
  SET_SEASON_YEAR = 'SET_SEASON_YEAR',
  ADD_CHAMPIONSHIP = 'ADD_CHAMPIONSHIP',
  ADD_EVENT = 'ADD_EVENT',
  ADD_EVENTS = 'ADD_EVENTS',
  ADD_TRACK = 'ADD_TRACK',
}

export enum GetterTypes {
  championship = 'championship',
  championships = 'championships',
  track = 'track',
  isLoading = 'isLoading',
  event = 'event',
  events = 'events',
  getNextRace = 'getNextRace',
  getPreviousRace = 'getPreviousRace',
  getPreviousRaceResults = 'getPreviousRaceResults',
  seasonYear = 'seasonYear',
}

export enum ActionTypes {
  fetchChampionships = 'fetchChampionships',
  fetchEvent = 'fetchEvent',
  fetchEvents = 'fetchEvents',
  fetchTrack = 'fetchTrack',
}

const moduleState: State = {
  isLoading: true,
  championships: [],
  events: [],
  event: undefined,
  track: undefined,
  seasonYear: 2021,
};

export type Mutations<S = State> = {
  [MutationTypes.ADD_CHAMPIONSHIP](state: S, payload: Array<ChampionshipResponseInterface>): void;
  [MutationTypes.SET_LOADING_STATE](state: S, payload: boolean): void;
  [MutationTypes.ADD_EVENT](state: S, payload: EventResponseInterface): void;
  [MutationTypes.ADD_EVENTS](state: S, payload: Array<EventResponseInterface>): void;
  [MutationTypes.ADD_TRACK](state: S, payload: TrackResponseInterface): void;
  [MutationTypes.SET_SEASON_YEAR](state: S, payload: number): void;
}

const mutations: MutationTree<State> & Mutations = {
  ADD_EVENT (state, payload) {
    state.event = new Event(payload);
  },
  ADD_EVENTS (state, payload) {
    payload.forEach((value: EventResponseInterface) => {
      state.events.push(new Event(value));
    });
  },
  ADD_TRACK (state, payload) {
    state.track = new Track(payload);
  },
  ADD_CHAMPIONSHIP (state, payload) {
    payload.forEach((championship: ChampionshipResponseInterface) => {
      state.championships.push(new Championship(championship));
    });
  },
  SET_LOADING_STATE (state, payload) {
    state.isLoading = payload;
  },
  SET_SEASON_YEAR (state, payload) {
    state.seasonYear = payload;
  },
};

type AugmentedActionContext = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [ActionTypes.fetchChampionships](
    store: AugmentedActionContext
  ): Promise<void>;
  [ActionTypes.fetchEvent](
    store: AugmentedActionContext,
    eventUrl: string,
  ): Promise<void>;
  [ActionTypes.fetchEvents](
    store: AugmentedActionContext,
  ): Promise<void>;
  [ActionTypes.fetchTrack](
    store: AugmentedActionContext,
    payload: FetchTrackPayload,
  ): Promise<void>;
}

const actions: ActionTree<State, RootState> & Actions = {
  fetchChampionships (store) {
    return new Promise((resolve, reject) => {
      const { commit, state, rootState } = store;
      commit(MutationTypes.SET_LOADING_STATE, true);

      Axios.get(`${AppConst.API_BASE_URL}/championship?_locale=${rootState.locale}&seasonYear=${state.seasonYear}`, {
        headers: {
          Accept: AppConst.API_JSON_HEADER,
        },
      }).then((response: AxiosResponse<JsonLd<ChampionshipResponseInterface>>) => {
        commit(MutationTypes.ADD_CHAMPIONSHIP, response.data[AppConst.DATA_HYDRA_MEMBER]);
        commit(MutationTypes.SET_LOADING_STATE, false);
        resolve();
      }).catch((e) => {
        reject(e);
      });
    });
  },
  fetchEvent (store, eventUrl) {
    return new Promise((resolve, reject) => {
      const { commit, state, rootState } = store;
      commit(MutationTypes.SET_LOADING_STATE, true);

      Axios.get(`${AppConst.API_BASE_HOST}${eventUrl}?_locale=${rootState.locale}&seasonYear=${state.seasonYear}`, {
        headers: {
          Accept: AppConst.API_JSON_HEADER,
        },
      }).then((response: AxiosResponse<EventResponseInterface>) => {
        commit(MutationTypes.ADD_EVENT, response.data);
        commit(MutationTypes.SET_LOADING_STATE, false);
        resolve();
      }).catch((e) => {
        reject(e);
      });
    });
  },
  fetchEvents (store) {
    return new Promise((resolve, reject) => {
      const { commit, state, rootState } = store;
      commit(MutationTypes.SET_LOADING_STATE, true);

      Axios.get(`${AppConst.API_BASE_URL}/events?_locale=${rootState.locale}&seasonYear=${state.seasonYear}`, {
        headers: {
          Accept: 'application/ld+json',
        },
      }).then((response: AxiosResponse<JsonLd<EventResponseInterface>>) => {
        commit(MutationTypes.ADD_EVENTS, response.data[AppConst.DATA_HYDRA_MEMBER]);
        commit(MutationTypes.SET_LOADING_STATE, false);
        resolve();
      }).catch((e) => {
        reject(e);
      });
    });
  },
  fetchTrack (store, payload) {
    return new Promise((resolve, reject) => {
      const {
        commit, dispatch, state, rootState,
      } = store;
      commit(MutationTypes.SET_LOADING_STATE, true);

      Axios.get(`${AppConst.API_BASE_URL}/event/tracks?_locale=${rootState.locale}&seasonYear=${state.seasonYear}`, {
        headers: {
          Accept: 'application/ld+json',
        },
      }).then((response: AxiosResponse<JsonLd<TrackResponseInterface>>) => {
        const track = response.data[AppConst.DATA_HYDRA_MEMBER].find((responseTrack: TrackResponseInterface) => Number(responseTrack.foreignId) === Number(payload.raceTrack));
        if (track) {
          commit(MutationTypes.ADD_TRACK, track);

          if (track.seasonEvent.length > 0) {
            dispatch(ActionTypes.fetchEvent, track.seasonEvent[Number(payload.raceTrackEventIndex || 0)])
              .then(() => {
                resolve();
              });
          } else {
            commit(MutationTypes.SET_LOADING_STATE, false);
            resolve();
          }
        }
      }).catch((e) => {
        reject(e);
      });
    });
  },
};

export type Getters<S = State> = {
  [GetterTypes.isLoading](state: S): boolean;
  [GetterTypes.championships](state: S): Array<Championship>;
  [GetterTypes.events](state: S): Array<Event>;
  [GetterTypes.getNextRace](state: S): Event | undefined;
  [GetterTypes.getPreviousRace](state: S): Event | undefined;
  [GetterTypes.getPreviousRaceResults](
    state: S,
    getters: {
      [K in keyof Getters]: ReturnType<Getters[K]>
    },
  ): Array<SessionResultInterface>;
  [GetterTypes.track](state: S): Track | undefined;
  [GetterTypes.event](state: S): Event | undefined;
  [GetterTypes.seasonYear](state: S): number;
  [GetterTypes.championship](state: S): Championship | undefined;
}

const getters: GetterTree<State, RootState> & Getters = {
  seasonYear (state) {
    return state.seasonYear;
  },
  isLoading (state) {
    return state.isLoading;
  },
  championships (state) {
    return state.championships;
  },
  championship (state) {
    return state.championships.find((championship) => championship.getYear() === state.seasonYear);
  },
  events (state) {
    return state.events;
  },
  getNextRace (state) {
    const { events } = state;
    let nextEvent: Event | undefined;
    events.forEach((event) => {
      if (event['@type'] === 'F1 Season Event') {
        const raceDate = new Date(event.raceStart);
        const now = new Date();
        const isNewer = !nextEvent || (nextEvent && new Date(nextEvent.raceStart) > raceDate);
        if (raceDate >= now && isNewer) {
          nextEvent = event;
        }
      }
    });

    return nextEvent;
  },
  getPreviousRace (state) {
    const { events } = state;
    let previousEvent: Event | undefined;
    events.forEach((event) => {
      if (event['@type'] === 'F1 Season Event' && event.raceStart) {
        const raceDate = new Date(event.raceStart);
        const now = new Date();
        const isNewer = !previousEvent || (previousEvent && new Date(previousEvent.raceStart) < raceDate);
        if (raceDate < now && isNewer) {
          previousEvent = event;
        }
      }
    });
    return previousEvent;
  },
  getPreviousRaceResults (state, moduleGetters) {
    const returnTracks: Array<SessionResultInterface> = [];
    const event: Event | undefined = moduleGetters.getPreviousRace;
    event?.seasonEventSessions.forEach((season: EventSessionInterface) => {
      if (season.sessionResult.length > 0 && Number(season.sessionTypeId) === 6 && season.sessionResult.length > 0) {
        season.sessionResult.forEach((sessionResult: SessionResultInterface) => {
          returnTracks.push(sessionResult);
        });
      }
    });

    return sortRaceResults(returnTracks);
  },
  track (state) {
    return state.track;
  },
  event (state) {
    return state.event;
  },
};

export type F1SeasonModuleStore = Omit<
  Module<State, RootState>,
  'getters' | 'mutations' | 'actions'
  > & {
  mutations<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
    key: K,
    payload: P,
    options?: CommitOptions,
  ): ReturnType<Mutations[K]>;
} & {
  getters: {
    [K in keyof Getters]: ReturnType<Getters[K]>
  };
} & {
  actions<K extends keyof Actions>(
    key: K,
    payload: Parameters<Actions[K]>[1],
    options?: DispatchOptions,
  ): ReturnType<Actions[K]>;
}

const f1SeasonStore: Module<State, RootState> = {
  state: moduleState,
  mutations,
  getters,
  actions,
};

export default f1SeasonStore;
